import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiFetch } from '@/lib/api'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Domain {
  id: string
  slug: string
  label: string
  icon: string
  description: string
  active: boolean
  featured: boolean
  sortOrder: number
  _count: { specialties: number }
}

interface SpecialtyConfig {
  specialty: string
  label: string
  shortLabel: string
  icon: string
  description: string
  category: 'EVENT' | 'TRAINING' | 'BOTH'
  active: boolean
  sortOrder: number
  subcategory: string | null
  keywords: string[]
  domainId: string | null
}

function parseKeywords(text: string): string[] {
  return [...new Set(text.split(/[,;\n]/).map((k) => k.trim().toLowerCase()).filter(Boolean))].slice(0, 20)
}

const CATEGORY_OPTIONS = [
  { value: 'EVENT', label: 'Événement', icon: '🎪', color: 'text-accent', bg: 'bg-accent/15 border-accent/30' },
  { value: 'TRAINING', label: 'Formation', icon: '🎓', color: 'text-success', bg: 'bg-success/15 border-success/30' },
  { value: 'BOTH', label: 'Les deux', icon: '✦', color: 'text-warning', bg: 'bg-warning/15 border-warning/30' },
]

const NO_DOMAIN = '__none__'
const EMPTY_SPECIALTY = { specialty: '', label: '', shortLabel: '', icon: '🔧', description: '', category: 'BOTH' as const, domainId: '', subcategory: '', keywords: '' }
const EMPTY_DOMAIN = { label: '', icon: '🔧', description: '', featured: false }

const INPUT = 'w-full bg-white/[0.06] border border-white/[0.12] rounded-xl px-3 py-2 text-text-primary text-sm'
const BTN_GHOST = 'px-3 py-1.5 rounded-xl text-sm text-text-muted border border-white/[0.08] hover:bg-white/[0.06] transition-all'
const BTN_ACCENT = 'px-4 py-1.5 rounded-xl text-sm bg-accent/15 border border-accent/30 text-accent font-medium hover:bg-accent/25 transition-all disabled:opacity-50'

function errMsg(e: unknown) {
  return e instanceof Error ? e.message : 'Erreur'
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DomainesSpecialitesPage() {
  const [domains, setDomains] = useState<Domain[]>([])
  const [configs, setConfigs] = useState<SpecialtyConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  // Spécialités
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Partial<SpecialtyConfig>>({})
  const [editKeywords, setEditKeywords] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [addForm, setAddForm] = useState({ ...EMPTY_SPECIALTY })
  const [addError, setAddError] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const addRef = useRef<HTMLDivElement>(null)

  // Domaines
  const [showAddDomain, setShowAddDomain] = useState(false)
  const [domainForm, setDomainForm] = useState({ ...EMPTY_DOMAIN })
  const [domainError, setDomainError] = useState('')
  const [editingDomainId, setEditingDomainId] = useState<string | null>(null)
  const [domainEditForm, setDomainEditForm] = useState<Partial<Domain>>({})
  const [domainDeleteConfirm, setDomainDeleteConfirm] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const load = useCallback(async () => {
    setError('')
    try {
      const [d, c] = await Promise.all([
        apiFetch<Domain[]>('/admin/domains'),
        apiFetch<SpecialtyConfig[]>('/admin/specialty-config'),
      ])
      setDomains(d)
      setConfigs(c)
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const flash = (key: string) => { setSaved(key); setTimeout(() => setSaved(null), 1500) }

  const byDomain = useMemo(() => {
    const map = new Map<string, SpecialtyConfig[]>()
    for (const c of configs) {
      const k = c.domainId ?? NO_DOMAIN
      if (!map.has(k)) map.set(k, [])
      map.get(k)!.push(c)
    }
    // Sous-catégories contiguës (ordre admin à l'intérieur) — les ▲▼ réordonnent au sein d'une sous-catégorie
    for (const list of map.values()) {
      list.sort((a, b) => (a.subcategory ?? '').localeCompare(b.subcategory ?? '', 'fr') || a.sortOrder - b.sortOrder)
    }
    return map
  }, [configs])

  const allSubcategories = useMemo(
    () => [...new Set(configs.map((c) => c.subcategory).filter((s): s is string => Boolean(s)))].sort((a, b) => a.localeCompare(b, 'fr')),
    [configs],
  )

  const sortedDomains = useMemo(() => [...domains].sort((a, b) => a.sortOrder - b.sortOrder), [domains])
  const orphans = byDomain.get(NO_DOMAIN) ?? []

  // ─── Actions spécialités ────────────────────────────────────────────────────

  async function patchConfig(specialty: string, patch: Partial<SpecialtyConfig>) {
    const current = configs.find((c) => c.specialty === specialty)
    if (!current) return
    setConfigs((prev) => prev.map((c) => (c.specialty === specialty ? { ...c, ...patch } : c)))
    setSaving(specialty)
    try {
      await apiFetch(`/admin/specialty-config/${specialty}`, { method: 'PATCH', body: JSON.stringify(patch) })
      flash(specialty)
    } catch (e) {
      setConfigs((prev) => prev.map((c) => (c.specialty === specialty ? current : c)))
      setError(errMsg(e))
    } finally {
      setSaving(null)
    }
  }

  async function saveEdit(specialty: string) {
    setSaving(specialty)
    const patch = { ...editForm, keywords: parseKeywords(editKeywords), subcategory: editForm.subcategory?.trim() || null }
    try {
      await apiFetch(`/admin/specialty-config/${specialty}`, { method: 'PATCH', body: JSON.stringify(patch) })
      setConfigs((prev) => prev.map((c) => (c.specialty === specialty ? { ...c, ...patch } : c)))
      flash(specialty)
      setEditingId(null)
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setSaving(null)
    }
  }

  async function createSpecialty() {
    setAddError('')
    const key = addForm.specialty.trim()
    if (!key) return setAddError('L\'identifiant est requis')
    if (!addForm.label.trim()) return setAddError('Le libellé est requis')
    if (!/^[A-Z0-9_]+$/.test(key)) return setAddError('Identifiant : majuscules, chiffres et underscores uniquement')
    if (configs.some((c) => c.specialty === key)) return setAddError('Cet identifiant existe déjà')
    setSaving('__new__')
    try {
      const created = await apiFetch<SpecialtyConfig>('/admin/specialty-config', {
        method: 'POST',
        body: JSON.stringify({ ...addForm, specialty: key, domainId: addForm.domainId || null, subcategory: addForm.subcategory.trim() || null, keywords: parseKeywords(addForm.keywords) }),
      })
      setConfigs((prev) => [...prev, created])
      setDomains((prev) => prev.map((d) => (d.id === created.domainId ? { ...d, _count: { specialties: d._count.specialties + 1 } } : d)))
      setShowAdd(false)
      setAddForm({ ...EMPTY_SPECIALTY, domainId: addForm.domainId })
    } catch (e) {
      setAddError(errMsg(e))
    } finally {
      setSaving(null)
    }
  }

  async function deleteSpecialty(specialty: string) {
    setSaving(specialty)
    try {
      await apiFetch(`/admin/specialty-config/${specialty}`, { method: 'DELETE' })
      const removed = configs.find((c) => c.specialty === specialty)
      setConfigs((prev) => prev.filter((c) => c.specialty !== specialty))
      if (removed?.domainId) {
        setDomains((prev) => prev.map((d) => (d.id === removed.domainId ? { ...d, _count: { specialties: d._count.specialties - 1 } } : d)))
      }
      setDeleteConfirm(null)
    } catch (e) {
      setError(errMsg(e))
      setDeleteConfirm(null)
    } finally {
      setSaving(null)
    }
  }

  async function moveSpecialty(specialty: string, domainId: string | null) {
    const current = configs.find((c) => c.specialty === specialty)
    if (!current || current.domainId === domainId) return
    await patchConfig(specialty, { domainId })
    setDomains((prev) => prev.map((d) => {
      if (d.id === current.domainId) return { ...d, _count: { specialties: d._count.specialties - 1 } }
      if (d.id === domainId) return { ...d, _count: { specialties: d._count.specialties + 1 } }
      return d
    }))
  }

  async function reorderSpecialty(specialty: string, direction: -1 | 1) {
    const current = configs.find((c) => c.specialty === specialty)
    if (!current) return
    const siblings = byDomain.get(current.domainId ?? NO_DOMAIN) ?? []
    const idx = siblings.findIndex((c) => c.specialty === specialty)
    const other = siblings[idx + direction]
    if (!other) return
    // Échange des sortOrder — si égaux (anciennes données), on force des valeurs distinctes
    const a = current.sortOrder === other.sortOrder ? other.sortOrder + direction : other.sortOrder
    const b = current.sortOrder === other.sortOrder ? current.sortOrder : current.sortOrder
    setConfigs((prev) => prev.map((c) =>
      c.specialty === current.specialty ? { ...c, sortOrder: a } : c.specialty === other.specialty ? { ...c, sortOrder: b } : c,
    ))
    setSaving(specialty)
    try {
      await Promise.all([
        apiFetch(`/admin/specialty-config/${current.specialty}`, { method: 'PATCH', body: JSON.stringify({ sortOrder: a }) }),
        apiFetch(`/admin/specialty-config/${other.specialty}`, { method: 'PATCH', body: JSON.stringify({ sortOrder: b }) }),
      ])
    } catch (e) {
      setError(errMsg(e))
      load()
    } finally {
      setSaving(null)
    }
  }

  // ─── Actions domaines ───────────────────────────────────────────────────────

  async function patchDomain(id: string, patch: Partial<Domain>) {
    const current = domains.find((d) => d.id === id)
    if (!current) return
    setDomains((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)))
    setSaving(id)
    try {
      await apiFetch(`/admin/domains/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
      flash(id)
    } catch (e) {
      setDomains((prev) => prev.map((d) => (d.id === id ? current : d)))
      setError(errMsg(e))
    } finally {
      setSaving(null)
    }
  }

  async function saveDomainEdit(id: string) {
    setSaving(id)
    try {
      const updated = await apiFetch<Domain>(`/admin/domains/${id}`, { method: 'PATCH', body: JSON.stringify(domainEditForm) })
      setDomains((prev) => prev.map((d) => (d.id === id ? { ...d, ...updated } : d)))
      flash(id)
      setEditingDomainId(null)
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setSaving(null)
    }
  }

  async function createDomain() {
    setDomainError('')
    if (!domainForm.label.trim()) return setDomainError('Le libellé est requis')
    setSaving('__new_domain__')
    try {
      const created = await apiFetch<Domain>('/admin/domains', { method: 'POST', body: JSON.stringify(domainForm) })
      setDomains((prev) => [...prev, created])
      setShowAddDomain(false)
      setDomainForm({ ...EMPTY_DOMAIN })
    } catch (e) {
      setDomainError(errMsg(e))
    } finally {
      setSaving(null)
    }
  }

  async function deleteDomain(id: string) {
    setSaving(id)
    try {
      await apiFetch(`/admin/domains/${id}`, { method: 'DELETE' })
      setDomains((prev) => prev.filter((d) => d.id !== id))
      setDomainDeleteConfirm(null)
    } catch (e) {
      setError(errMsg(e))
      setDomainDeleteConfirm(null)
    } finally {
      setSaving(null)
    }
  }

  async function reorderDomain(id: string, direction: -1 | 1) {
    const idx = sortedDomains.findIndex((d) => d.id === id)
    const current = sortedDomains[idx]
    const other = sortedDomains[idx + direction]
    if (!current || !other) return
    const a = current.sortOrder === other.sortOrder ? other.sortOrder + direction : other.sortOrder
    const b = current.sortOrder
    setDomains((prev) => prev.map((d) => (d.id === current.id ? { ...d, sortOrder: a } : d.id === other.id ? { ...d, sortOrder: b } : d)))
    setSaving(id)
    try {
      await Promise.all([
        apiFetch(`/admin/domains/${current.id}`, { method: 'PATCH', body: JSON.stringify({ sortOrder: a }) }),
        apiFetch(`/admin/domains/${other.id}`, { method: 'PATCH', body: JSON.stringify({ sortOrder: b }) }),
      ])
    } catch (e) {
      setError(errMsg(e))
      load()
    } finally {
      setSaving(null)
    }
  }

  // ─── Rendu ──────────────────────────────────────────────────────────────────

  const domainSelect = (value: string | null, onChange: (v: string | null) => void, disabled?: boolean) => (
    <select
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value || null)}
      className="bg-white/[0.06] border border-white/[0.12] rounded-lg px-2 py-1 text-xs text-text-secondary disabled:opacity-50"
      title="Domaine"
    >
      <option value="">Sans domaine</option>
      {sortedDomains.map((d) => <option key={d.id} value={d.id}>{d.icon} {d.label}</option>)}
    </select>
  )

  function SpecialtyCard({ cfg, index, total }: { cfg: SpecialtyConfig; index: number; total: number }) {
    const isSaving = saving === cfg.specialty
    const isSaved = saved === cfg.specialty
    const isEditing = editingId === cfg.specialty
    const isDeleteConfirm = deleteConfirm === cfg.specialty

    if (isEditing) {
      return (
        <div className="glass-card p-5 flex flex-col gap-4">
          <div className="grid grid-cols-[64px_1fr_1fr] gap-3">
            <div>
              <label className="text-[11px] text-text-muted mb-1 block">Icône</label>
              <input value={editForm.icon ?? cfg.icon} onChange={(e) => setEditForm((f) => ({ ...f, icon: e.target.value }))} className={`${INPUT} text-center`} maxLength={4} />
            </div>
            <div>
              <label className="text-[11px] text-text-muted mb-1 block">Libellé complet</label>
              <input value={editForm.label ?? cfg.label} onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))} className={INPUT} placeholder="Ex: Agent SSIAP 1" />
            </div>
            <div>
              <label className="text-[11px] text-text-muted mb-1 block">Libellé court</label>
              <input value={editForm.shortLabel ?? cfg.shortLabel} onChange={(e) => setEditForm((f) => ({ ...f, shortLabel: e.target.value }))} className={INPUT} placeholder="Ex: SSIAP 1" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-text-muted mb-1 block">Description</label>
            <textarea value={editForm.description ?? cfg.description} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} rows={2} className={`${INPUT} resize-none`} placeholder="Description courte…" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-3">
            <div>
              <label className="text-[11px] text-text-muted mb-1 block">Sous-catégorie</label>
              <input list="velink-subcategories" value={editForm.subcategory ?? cfg.subcategory ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, subcategory: e.target.value }))} className={INPUT} placeholder="Ex: Transport de personnes" />
            </div>
            <div>
              <label className="text-[11px] text-text-muted mb-1 block">Mots-clés de recherche (séparés par des virgules)</label>
              <input value={editKeywords} onChange={(e) => setEditKeywords(e.target.value)} className={INPUT} placeholder="Ex: vtc, navette, permis b" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-text-muted">Domaine :</span>
              {domainSelect(editForm.domainId === undefined ? cfg.domainId : editForm.domainId, (v) => setEditForm((f) => ({ ...f, domainId: v })))}
              <span className="text-[11px] text-text-muted ml-2">Catégorie :</span>
              {CATEGORY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setEditForm((f) => ({ ...f, category: opt.value as SpecialtyConfig['category'] }))}
                  className={['flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all', (editForm.category ?? cfg.category) === opt.value ? `${opt.bg} ${opt.color}` : 'bg-white/[0.04] border-white/[0.08] text-text-muted hover:bg-white/[0.07]'].join(' ')}
                >
                  {opt.icon} {opt.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { setEditingId(null); setEditForm({}) }} className={BTN_GHOST}>Annuler</button>
              <button onClick={() => saveEdit(cfg.specialty)} disabled={isSaving} className={BTN_ACCENT}>{isSaving ? 'Enregistrement…' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className={`glass-card transition-all ${!cfg.active ? 'opacity-60' : ''}`}>
        <div className="p-4 flex items-center gap-3">
          <div className="flex flex-col gap-0.5 flex-shrink-0">
            <button onClick={() => reorderSpecialty(cfg.specialty, -1)} disabled={index === 0 || isSaving} className="text-[10px] leading-none text-text-muted hover:text-text-primary disabled:opacity-20" title="Monter">▲</button>
            <button onClick={() => reorderSpecialty(cfg.specialty, 1)} disabled={index === total - 1 || isSaving} className="text-[10px] leading-none text-text-muted hover:text-text-primary disabled:opacity-20" title="Descendre">▼</button>
          </div>
          <div className="w-10 h-10 rounded-2xl bg-white/[0.06] border border-white/[0.10] flex items-center justify-center text-xl flex-shrink-0">{cfg.icon || '🔧'}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-text-primary font-semibold text-sm">{cfg.label || cfg.specialty}</span>
              {cfg.shortLabel && cfg.shortLabel !== cfg.label && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.07] text-text-muted border border-white/[0.08]">{cfg.shortLabel}</span>
              )}
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/20 text-text-muted font-mono">{cfg.specialty}</span>
              {cfg.keywords?.length > 0 && (
                <span className="text-[10px] text-text-muted" title={cfg.keywords.join(', ')}>🔎 {cfg.keywords.length} mot{cfg.keywords.length > 1 ? 's' : ''}-clé{cfg.keywords.length > 1 ? 's' : ''}</span>
              )}
            </div>
            {cfg.description && <p className="text-text-muted text-xs mt-0.5 truncate">{cfg.description}</p>}
          </div>
          <div className="hidden lg:block flex-shrink-0">{domainSelect(cfg.domainId, (v) => moveSpecialty(cfg.specialty, v), isSaving)}</div>
          <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
            {CATEGORY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => patchConfig(cfg.specialty, { category: opt.value as SpecialtyConfig['category'] })}
                disabled={isSaving}
                className={['flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border transition-all', cfg.category === opt.value ? `${opt.bg} ${opt.color}` : 'bg-white/[0.04] border-white/[0.08] text-text-muted hover:text-text-secondary hover:bg-white/[0.07]'].join(' ')}
                title={opt.label}
              >
                {opt.icon}<span className="hidden xl:inline"> {opt.label}</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => patchConfig(cfg.specialty, { active: !cfg.active })}
            disabled={isSaving}
            className={['relative w-10 h-5 rounded-full border transition-all flex-shrink-0', cfg.active ? 'bg-success/30 border-success/40' : 'bg-white/[0.06] border-white/[0.12]'].join(' ')}
            title={cfg.active ? 'Active' : 'Inactive'}
          >
            <span className={['absolute top-0.5 w-4 h-4 rounded-full transition-all', cfg.active ? 'left-5 bg-success' : 'left-0.5 bg-text-muted'].join(' ')} />
          </button>
          <span className="w-5 text-center flex-shrink-0">
            {isSaving ? <span className="w-4 h-4 border border-accent/30 border-t-accent rounded-full animate-spin inline-block" /> : isSaved ? <span className="text-success text-sm">✓</span> : null}
          </span>
          {isDeleteConfirm ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-danger">Confirmer ?</span>
              <button onClick={() => deleteSpecialty(cfg.specialty)} disabled={isSaving} className="px-2.5 py-1 rounded-lg text-xs bg-danger/15 border border-danger/30 text-danger hover:bg-danger/25 transition-all">Supprimer</button>
              <button onClick={() => setDeleteConfirm(null)} className="px-2.5 py-1 rounded-lg text-xs text-text-muted border border-white/[0.08] hover:bg-white/[0.06] transition-all">Annuler</button>
            </div>
          ) : (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => { setEditingId(cfg.specialty); setEditKeywords((cfg.keywords ?? []).join(', ')); setEditForm({ label: cfg.label, shortLabel: cfg.shortLabel, icon: cfg.icon, description: cfg.description, category: cfg.category, domainId: cfg.domainId, subcategory: cfg.subcategory }) }}
                className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-white/[0.08] transition-all text-sm"
                title="Modifier"
              >✏️</button>
              <button onClick={() => setDeleteConfirm(cfg.specialty)} className="p-1.5 rounded-lg text-text-muted hover:text-danger hover:bg-danger/10 transition-all text-sm" title="Supprimer">🗑️</button>
            </div>
          )}
        </div>
      </div>
    )
  }

  function DomainSection({ domain, index }: { domain: Domain; index: number }) {
    const list = byDomain.get(domain.id) ?? []
    const isSaving = saving === domain.id
    const isSaved = saved === domain.id
    const isEditing = editingDomainId === domain.id
    const isCollapsed = collapsed[domain.id]
    const isDeleteConfirm = domainDeleteConfirm === domain.id

    return (
      <section className={`rounded-2xl border ${domain.active ? 'border-white/[0.10]' : 'border-white/[0.06] opacity-70'} bg-white/[0.02]`}>
        {isEditing ? (
          <div className="p-5 flex flex-col gap-4">
            <div className="grid grid-cols-[64px_1fr_1fr] gap-3">
              <div>
                <label className="text-[11px] text-text-muted mb-1 block">Icône</label>
                <input value={domainEditForm.icon ?? domain.icon} onChange={(e) => setDomainEditForm((f) => ({ ...f, icon: e.target.value }))} className={`${INPUT} text-center`} maxLength={4} />
              </div>
              <div>
                <label className="text-[11px] text-text-muted mb-1 block">Libellé</label>
                <input value={domainEditForm.label ?? domain.label} onChange={(e) => setDomainEditForm((f) => ({ ...f, label: e.target.value }))} className={INPUT} />
              </div>
              <div>
                <label className="text-[11px] text-text-muted mb-1 block">Slug (URL)</label>
                <input value={domainEditForm.slug ?? domain.slug} onChange={(e) => setDomainEditForm((f) => ({ ...f, slug: e.target.value }))} className={`${INPUT} font-mono`} />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-text-muted mb-1 block">Description (affichée sur l'accueil et la page experts)</label>
              <textarea value={domainEditForm.description ?? domain.description} onChange={(e) => setDomainEditForm((f) => ({ ...f, description: e.target.value }))} rows={2} className={`${INPUT} resize-none`} />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => { setEditingDomainId(null); setDomainEditForm({}) }} className={BTN_GHOST}>Annuler</button>
              <button onClick={() => saveDomainEdit(domain.id)} disabled={isSaving} className={BTN_ACCENT}>{isSaving ? 'Enregistrement…' : 'Enregistrer'}</button>
            </div>
          </div>
        ) : (
          <div className="px-4 py-3 flex items-center gap-3">
            <div className="flex flex-col gap-0.5 flex-shrink-0">
              <button onClick={() => reorderDomain(domain.id, -1)} disabled={index === 0 || isSaving} className="text-[10px] leading-none text-text-muted hover:text-text-primary disabled:opacity-20" title="Monter">▲</button>
              <button onClick={() => reorderDomain(domain.id, 1)} disabled={index === sortedDomains.length - 1 || isSaving} className="text-[10px] leading-none text-text-muted hover:text-text-primary disabled:opacity-20" title="Descendre">▼</button>
            </div>
            <button onClick={() => setCollapsed((c) => ({ ...c, [domain.id]: !isCollapsed }))} className="text-text-muted text-xs w-4" title={isCollapsed ? 'Déplier' : 'Replier'}>{isCollapsed ? '▶' : '▼'}</button>
            <span className="text-2xl">{domain.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-text-primary font-bold">{domain.label}</h2>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/20 text-text-muted font-mono">/{domain.slug}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.07] text-text-muted border border-white/[0.08]">{list.length} spécialité{list.length > 1 ? 's' : ''}</span>
              </div>
              {domain.description && <p className="text-text-muted text-xs mt-0.5 truncate">{domain.description}</p>}
            </div>
            <button
              onClick={() => patchDomain(domain.id, { featured: !domain.featured })}
              disabled={isSaving}
              className={['px-2.5 py-1 rounded-lg text-xs font-medium border transition-all flex-shrink-0', domain.featured ? 'bg-warning/15 border-warning/30 text-warning' : 'bg-white/[0.04] border-white/[0.08] text-text-muted hover:bg-white/[0.07]'].join(' ')}
              title="Affiché parmi les domaines mis en avant sur l'accueil"
            >
              ★ <span className="hidden md:inline">{domain.featured ? 'Mis en avant' : 'Mettre en avant'}</span>
            </button>
            <button
              onClick={() => patchDomain(domain.id, { active: !domain.active })}
              disabled={isSaving}
              className={['relative w-10 h-5 rounded-full border transition-all flex-shrink-0', domain.active ? 'bg-success/30 border-success/40' : 'bg-white/[0.06] border-white/[0.12]'].join(' ')}
              title={domain.active ? 'Actif' : 'Inactif (masqué partout, spécialités comprises)'}
            >
              <span className={['absolute top-0.5 w-4 h-4 rounded-full transition-all', domain.active ? 'left-5 bg-success' : 'left-0.5 bg-text-muted'].join(' ')} />
            </button>
            <span className="w-5 text-center flex-shrink-0">
              {isSaving ? <span className="w-4 h-4 border border-accent/30 border-t-accent rounded-full animate-spin inline-block" /> : isSaved ? <span className="text-success text-sm">✓</span> : null}
            </span>
            {isDeleteConfirm ? (
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-danger">Confirmer ?</span>
                <button onClick={() => deleteDomain(domain.id)} disabled={isSaving} className="px-2.5 py-1 rounded-lg text-xs bg-danger/15 border border-danger/30 text-danger hover:bg-danger/25 transition-all">Supprimer</button>
                <button onClick={() => setDomainDeleteConfirm(null)} className="px-2.5 py-1 rounded-lg text-xs text-text-muted border border-white/[0.08] hover:bg-white/[0.06] transition-all">Annuler</button>
              </div>
            ) : (
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => { setShowAdd(true); setAddForm((f) => ({ ...f, domainId: domain.id })); setTimeout(() => addRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50) }}
                  className="p-1.5 rounded-lg text-text-muted hover:text-accent hover:bg-accent/10 transition-all text-sm"
                  title="Ajouter une spécialité dans ce domaine"
                >＋</button>
                <button onClick={() => { setEditingDomainId(domain.id); setDomainEditForm({ label: domain.label, slug: domain.slug, icon: domain.icon, description: domain.description }) }} className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-white/[0.08] transition-all text-sm" title="Modifier le domaine">✏️</button>
                <button
                  onClick={() => setDomainDeleteConfirm(domain.id)}
                  disabled={list.length > 0}
                  className="p-1.5 rounded-lg text-text-muted hover:text-danger hover:bg-danger/10 transition-all text-sm disabled:opacity-30 disabled:hover:text-text-muted disabled:hover:bg-transparent"
                  title={list.length > 0 ? 'Déplacez d\'abord ses spécialités' : 'Supprimer le domaine'}
                >🗑️</button>
              </div>
            )}
          </div>
        )}

        {!isCollapsed && !isEditing && (
          <div className="px-4 pb-4 flex flex-col gap-2">
            {list.length === 0 ? (
              <p className="text-text-muted text-xs py-3 text-center">Aucune spécialité — utilisez « ＋ » pour en ajouter une ici.</p>
            ) : list.map((cfg, i) => {
              const sub = cfg.subcategory ?? ''
              const showSub = i === 0 || (list[i - 1].subcategory ?? '') !== sub
              return (
                <div key={cfg.specialty} className={showSub && i > 0 ? 'pt-2' : ''}>
                  {showSub && (
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1.5 ml-1">
                      {sub || 'Sans sous-catégorie'}
                    </p>
                  )}
                  <SpecialtyCard cfg={cfg} index={i} total={list.length} />
                </div>
              )
            })}
          </div>
        )}
      </section>
    )
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Domaines & spécialités</h1>
          <p className="text-text-secondary text-sm max-w-2xl">
            Les <strong className="text-text-primary">domaines</strong> structurent l'offre (Sécurité, Événementiel, Formation…) et alimentent le bouton
            « Trouver un expert » de l'accueil. Chaque domaine regroupe des <strong className="text-text-primary">spécialités</strong> que les prestataires
            cochent sur leur profil et que les entreprises choisissent à la création d'une mission.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => setShowAddDomain((v) => !v)} className={BTN_GHOST}>+ Domaine</button>
          <button
            onClick={() => { setShowAdd(true); setAddForm((f) => ({ ...f, domainId: f.domainId || sortedDomains[0]?.id || '' })); setTimeout(() => addRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50) }}
            className={BTN_ACCENT}
          >+ Spécialité</button>
        </div>
      </div>

      <datalist id="velink-subcategories">
        {allSubcategories.map((s) => <option key={s} value={s} />)}
      </datalist>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-danger/70 hover:text-danger">✕</button>
        </div>
      )}

      {showAddDomain && (
        <div className="mb-6 glass-card p-5 border-accent/20 bg-accent/[0.03]">
          <h2 className="text-base font-semibold text-text-primary mb-4">Nouveau domaine</h2>
          <div className="grid grid-cols-[64px_1fr_1fr] gap-3 mb-3">
            <div>
              <label className="text-[11px] text-text-muted mb-1 block">Icône</label>
              <input value={domainForm.icon} onChange={(e) => setDomainForm((f) => ({ ...f, icon: e.target.value }))} className={`${INPUT} text-center`} maxLength={4} />
            </div>
            <div>
              <label className="text-[11px] text-text-muted mb-1 block">Libellé *</label>
              <input value={domainForm.label} onChange={(e) => setDomainForm((f) => ({ ...f, label: e.target.value }))} className={INPUT} placeholder="Ex: Événementiel" autoFocus />
            </div>
            <div>
              <label className="text-[11px] text-text-muted mb-1 block">Description</label>
              <input value={domainForm.description} onChange={(e) => setDomainForm((f) => ({ ...f, description: e.target.value }))} className={INPUT} placeholder="Une phrase pour l'accueil" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
              <input type="checkbox" checked={domainForm.featured} onChange={(e) => setDomainForm((f) => ({ ...f, featured: e.target.checked }))} className="accent-[#6E7FFF]" />
              ★ Mettre en avant sur l'accueil
            </label>
            <div className="flex items-center gap-2">
              {domainError && <span className="text-xs text-danger">{domainError}</span>}
              <button onClick={() => { setShowAddDomain(false); setDomainForm({ ...EMPTY_DOMAIN }); setDomainError('') }} className={BTN_GHOST}>Annuler</button>
              <button onClick={createDomain} disabled={saving === '__new_domain__'} className={BTN_ACCENT}>{saving === '__new_domain__' ? 'Création…' : 'Créer le domaine'}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {sortedDomains.map((d, i) => <DomainSection key={d.id} domain={d} index={i} />)}

          {orphans.length > 0 && (
            <section className="rounded-2xl border border-warning/25 bg-warning/[0.03]">
              <div className="px-4 py-3">
                <h2 className="text-text-primary font-bold">🧩 Sans domaine <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-white/[0.07] text-text-muted border border-white/[0.08] ml-1">{orphans.length}</span></h2>
                <p className="text-text-muted text-xs mt-0.5">Ces spécialités n'apparaissent dans aucun domaine sur l'accueil — rattachez-les via le sélecteur.</p>
              </div>
              <div className="px-4 pb-4 flex flex-col gap-2">
                {orphans.map((cfg, i) => <SpecialtyCard key={cfg.specialty} cfg={cfg} index={i} total={orphans.length} />)}
              </div>
            </section>
          )}
        </div>
      )}

      {showAdd && (
        <div ref={addRef} className="mt-6 glass-card p-6 border-accent/20 bg-accent/[0.03]">
          <h2 className="text-base font-semibold text-text-primary mb-4">Nouvelle spécialité</h2>
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-[200px_64px_1fr_1fr] gap-3">
              <div>
                <label className="text-[11px] text-text-muted mb-1 block">Identifiant (clé unique) *</label>
                <input
                  value={addForm.specialty}
                  onChange={(e) => setAddForm((f) => ({ ...f, specialty: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') }))}
                  className={`${INPUT} font-mono`}
                  placeholder="EX_PRESTATION"
                />
              </div>
              <div>
                <label className="text-[11px] text-text-muted mb-1 block">Icône</label>
                <input value={addForm.icon} onChange={(e) => setAddForm((f) => ({ ...f, icon: e.target.value }))} className={`${INPUT} text-center`} maxLength={4} />
              </div>
              <div>
                <label className="text-[11px] text-text-muted mb-1 block">Libellé complet *</label>
                <input value={addForm.label} onChange={(e) => setAddForm((f) => ({ ...f, label: e.target.value }))} className={INPUT} placeholder="Ex: Agent SSIAP 1" />
              </div>
              <div>
                <label className="text-[11px] text-text-muted mb-1 block">Libellé court</label>
                <input value={addForm.shortLabel} onChange={(e) => setAddForm((f) => ({ ...f, shortLabel: e.target.value }))} className={INPUT} placeholder="Ex: SSIAP 1" />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-text-muted mb-1 block">Description</label>
              <textarea value={addForm.description} onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))} rows={2} className={`${INPUT} resize-none`} placeholder="Description courte de la spécialité…" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-3">
              <div>
                <label className="text-[11px] text-text-muted mb-1 block">Sous-catégorie</label>
                <input list="velink-subcategories" value={addForm.subcategory} onChange={(e) => setAddForm((f) => ({ ...f, subcategory: e.target.value }))} className={INPUT} placeholder="Ex: Transport de personnes" />
              </div>
              <div>
                <label className="text-[11px] text-text-muted mb-1 block">Mots-clés de recherche (séparés par des virgules)</label>
                <input value={addForm.keywords} onChange={(e) => setAddForm((f) => ({ ...f, keywords: e.target.value }))} className={INPUT} placeholder="Ex: vtc, navette, permis b" />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-text-muted">Domaine :</span>
                {domainSelect(addForm.domainId || null, (v) => setAddForm((f) => ({ ...f, domainId: v ?? '' })))}
                <span className="text-[11px] text-text-muted ml-2">Catégorie :</span>
                {CATEGORY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setAddForm((f) => ({ ...f, category: opt.value as typeof addForm.category }))}
                    className={['flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all', addForm.category === opt.value ? `${opt.bg} ${opt.color}` : 'bg-white/[0.04] border-white/[0.08] text-text-muted hover:bg-white/[0.07]'].join(' ')}
                  >
                    {opt.icon} {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {addError && <span className="text-xs text-danger">{addError}</span>}
                <button onClick={() => { setShowAdd(false); setAddForm({ ...EMPTY_SPECIALTY }); setAddError('') }} className={BTN_GHOST}>Annuler</button>
                <button onClick={createSpecialty} disabled={saving === '__new__'} className={BTN_ACCENT}>{saving === '__new__' ? 'Création…' : 'Créer la spécialité'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 glass-card p-5 border-accent/20 bg-accent/[0.04]">
        <p className="text-text-secondary text-sm">
          <strong className="text-text-primary">Comment ça marche :</strong> un domaine <span className="text-warning">★ mis en avant</span> apparaît
          parmi les raccourcis « Trouver un expert » de l'accueil. Une spécialité <em>inactive</em> (ou dont le domaine est inactif) n'apparaît nulle part,
          sans rien supprimer. À la création d'une mission de type
          <span className="mx-1 px-2 py-0.5 rounded bg-accent/15 text-accent text-xs font-medium">Événement</span>
          seules les spécialités <em>Événement</em> ou <em>Les deux</em> s'affichent ; idem pour
          <span className="mx-1 px-2 py-0.5 rounded bg-success/15 text-success text-xs font-medium">Formation</span>.
        </p>
      </div>
    </div>
  )
}
